import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { theme, card, btnPrimary, inputStyle, floatingSheet } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { InviteHistoryTable } from "../components/InviteHistoryTable";
import type { InviteSummary } from "../components/InviteHistoryTable";
import { FRAMEWORK_LABELS, useFrameworks } from "../context/FrameworksContext";
import { DirectorySyncPanel } from "../components/DirectorySyncPanel";
import { SignInPolicyPanel } from "../components/SignInPolicyPanel";
import { DimensionHeading, IndexTabs } from "../components/Drawing";
import { OwnedRecordsSheet, type OwnedRecord } from "../components/OwnedRecordsSheet";

import { cbPalette, cbSafe } from "../lib/colourBlind";
// ── Types ─────────────────────────────────────────────────────────────────────

interface Role {
  id: string;
  name: string;
  description: string | null;
  color: string;
  permissions: string[];
  frameworkIds: string[];
  isSystem: boolean;
  builtinKey: string | null;
  memberCount?: number;
  /** False for a built-in role the organisation's edition does not carry (Jersey: no quality, health and safety, AI or data protection lead). Existing holders keep it; it is not offered for a new assignment. */
  offered?: boolean;
}

interface CatalogueGroup {
  group: string;
  // impliedBy: broad permissions that already include this one (backend middleware/permissions.ts IMPLIED_BY).
  items: Array<{ key: string; label: string; description: string; impliedBy?: string[] }>;
}

// Built-in roles whose framework scope is chosen per member rather than on the role
// (backend lib/orgRoles.ts PER_MEMBER_FRAMEWORK_ROLES).
const PER_MEMBER_ROLES: ReadonlySet<string> = new Set(["framework_specialist", "external_reviewer"]);
const isPerMemberRole = (key: string | null | undefined) => !!key && PER_MEMBER_ROLES.has(key);

interface RolesData {
  roles: Role[];
  catalogue: CatalogueGroup[];
  canManage: boolean;
}

interface Department { id: string; name: string; color: string; }
interface SiteRef { id: string; name: string; country: string; }
interface Tag { id: string; name: string; color: string; source?: string | null; }   // source set on tags directory sync manages

interface Member {
  id: string;
  displayName: string;
  email: string;
  isOrgAdmin: boolean;
  clientRole: string;
  role: { id: string; name: string; color: string; isSystem: boolean; builtinKey: string | null } | null;
  frameworkAccess: string[];
  departments: Department[];
  sites: SiteRef[];
  tags: Tag[];
  jobTitle: string | null;
  location: string | null;
  // Set while directory sync manages the role (label of the rule that matched).
  directoryRoleSource: string | null;
  // How the member signs in; "password" accounts carry an authenticator app an admin can reset.
  signInMethod: "microsoft" | "google" | "saml" | "password" | "none";
  totpEnabled: boolean;
  createdAt: string;
}

const SIGN_IN_LABEL: Record<Member["signInMethod"], string> = {
  microsoft: "Microsoft", google: "Google", saml: "SAML", password: "Password + app", none: "Not signed in yet",
};

// Consultancy staff on the consultancy's own organisation — they reach this
// portal through the consultant workspace, not as members.
interface StaffMember {
  id: string;
  displayName: string;
  email: string;
  consultantRole: string;
  jobTitle: string | null;
  location: string | null;
  createdAt: string;
}

interface TeamData {
  members: Member[];
  // Removed members: deactivated, never deleted, so what they did still names them.
  deactivated?: Member[];
  isOrgAdmin: boolean;
  staff?: StaffMember[];
  consultancyName?: string | null;
}

const CONSULTANT_ROLE_LABELS: Record<string, string> = {
  lead_consultant:      "Lead consultant",
  senior_consultant:    "Senior consultant",
  junior_consultant:    "Junior consultant",
  framework_specialist: "Framework specialist",
  report_writer:        "Report writer",
};

const PALETTE = cbPalette([
  "#c026d3", "#8b5cf6", "#3b82f6", "#0ea5e9", "#10b981", "#84cc16",
  "#f59e0b", "#f97316", "#ef4444", "#ec4899", "#14b8a6", "#64748b",
]);

const errorMessage = apiErrorMessage;

// ── Small shared pieces ───────────────────────────────────────────────────────

function Chip({ name, color: stored, small }: { name: string; color: string; small?: boolean }) {
  const color = cbSafe(stored);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: small ? "1px 7px" : "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600,
      background: color + "22", color, border: `1px solid ${color}55`, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {name}
    </span>
  );
}

function TogglePills<T extends { id: string; name: string; color?: string }>({ items, selected, onToggle, emptyText }: {
  items: T[];
  selected: string[];
  onToggle: (id: string) => void;
  emptyText?: string;
}) {
  if (items.length === 0) return <div style={{ fontSize: 11, color: theme.color.subtle }}>{emptyText ?? "Nothing to choose from yet."}</div>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {items.map((it) => {
        const sel = selected.includes(it.id);
        const colour = it.color ?? theme.color.accent;
        return (
          <button
            key={it.id} type="button" onClick={() => onToggle(it.id)}
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontWeight: sel ? 600 : 400,
              border: `1px solid ${sel ? colour : theme.color.border}`,
              background: sel ? colour + "22" : "transparent",
              color: sel ? colour : theme.color.muted,
            }}
          >
            {it.name}
          </button>
        );
      })}
    </div>
  );
}

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {PALETTE.map((c) => (
        <button
          key={c} type="button" onClick={() => onChange(c)}
          style={{
            width: 20, height: 20, borderRadius: "50%", border: "none", background: c, cursor: "pointer",
            outline: value === c ? `2px solid ${theme.color.text}` : "2px solid transparent", outlineOffset: 2,
          }}
        />
      ))}
    </div>
  );
}

function Modal({ title, onClose, children, width = 520 }: { title: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  // Escape closes, as every other dialog does; screen readers are told a dialog opened.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog" aria-modal="true" aria-label={title}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: width, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{title}</div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
const hintStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, marginTop: 2 };
const secondaryBtn: React.CSSProperties = { ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" };
const smallBtn: React.CSSProperties = {
  padding: "4px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`,
  background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer",
};
// The members table scrolls sideways on a narrow window: Edit / Remove stay pinned at the right
// and a confirmation stays at the left of what is in view, never past the scroll.
const stickyActions: React.CSSProperties = {
  position: "sticky", right: 0, background: "var(--c-panel)", boxShadow: "var(--c-sheetShadow)",
};
const inViewConfirm: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  position: "sticky", left: 16, maxWidth: "min(560px, calc(100vw - 120px))",
};

function useToggleList(initial: string[] = []) {
  const [list, setList] = useState<string[]>(initial);
  const toggle = (id: string) => setList((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  return [list, toggle, setList] as const;
}

// ── Role picker (shared by invite + edit) ─────────────────────────────────────

function RoleFields({ roles, roleId, setRoleId, frameworkIds, toggleFramework, enrolled, disabled, disabledHint }: {
  roles: Role[];
  roleId: string;
  setRoleId: (id: string) => void;
  frameworkIds: string[];
  toggleFramework: (id: string) => void;
  enrolled: Array<{ id: string; name: string }>;
  disabled?: boolean;
  disabledHint?: string;
}) {
  const role = roles.find((r) => r.id === roleId);
  const isSpecialist = isPerMemberRole(role?.builtinKey);
  const isAdmin = role?.permissions.includes("org:admin");
  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle}>Role</label>
        <select style={inputStyle} value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={disabled}>
          {roles.filter((r) => r.offered !== false || r.id === roleId).map((r) => (
            <option key={r.id} value={r.id}>{r.name}{r.isSystem ? "" : " (custom)"}</option>
          ))}
        </select>
        <div style={hintStyle}>
          {disabled && disabledHint ? disabledHint
            : role?.description || (isAdmin ? "Can manage team members and all compliance data." : "Access limited to the permissions on this role.")}
          {role && role.frameworkIds.length > 0 && !isSpecialist && (
            <> Restricted to {role.frameworkIds.map((f) => (FRAMEWORK_LABELS as Record<string, string>)[f] ?? f).join(", ")}.</>
          )}
        </div>
      </div>
      {isSpecialist && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Framework access</label>
          <TogglePills items={enrolled} selected={frameworkIds} onToggle={toggleFramework} emptyText="No frameworks enrolled yet." />
          {frameworkIds.length === 0 && <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 2 }}>Select at least one framework.</div>}
        </div>
      )}
    </>
  );
}

// ── Invite modal ──────────────────────────────────────────────────────────────

function InviteMemberModal({ roles, departments, tags, enrolled, onClose, onDone }: {
  roles: Role[];
  departments: Department[];
  tags: Tag[];
  enrolled: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const viewer = roles.find((r) => r.builtinKey === "viewer") ?? roles.find((r) => r.offered !== false) ?? roles[0];
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(viewer?.id ?? "");
  useEffect(() => { if (!roleId && viewer) setRoleId(viewer.id); }, [roleId, viewer]);
  const [frameworkIds, toggleFramework, setFrameworkIds] = useToggleList();
  const [departmentIds, toggleDepartment] = useToggleList();
  const [tagIds, toggleTag] = useToggleList();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const role = roles.find((r) => r.id === roleId);
  const isSpecialist = isPerMemberRole(role?.builtinKey);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await api.post("/invites/org-member", {
        email: email.trim(),
        roleId,
        frameworkIds: isSpecialist ? frameworkIds : [],
        departmentIds,
        tagIds,
      });
      setDone(true);
      onDone();
    } catch (err) {
      setError(errorMessage(err, "Failed to send invite"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={done ? "Invite sent" : "Invite team member"} onClose={onClose}>
      {done ? (
        <div style={{ background: theme.color.successBg, border: `1px solid ${theme.color.success}`, borderRadius: 3, padding: "12px 16px", fontSize: 13, color: theme.color.successText }}>
          Invite sent to <strong>{email}</strong>.
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={email} autoFocus required onChange={(e) => setEmail(e.target.value)} placeholder="colleague@company.com" />
          </div>

          <RoleFields
            roles={roles} roleId={roleId} setRoleId={(id) => { setRoleId(id); setFrameworkIds([]); }}
            frameworkIds={frameworkIds} toggleFramework={toggleFramework} enrolled={enrolled}
          />

          <div style={fieldStyle}>
            <label style={labelStyle}>Departments</label>
            <TogglePills items={departments} selected={departmentIds} onToggle={toggleDepartment} emptyText="No departments yet." />
            <div style={hintStyle}>Members of a department see controls tagged to it (plus untagged controls). Leave empty to see everything.</div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Tags</label>
            <TogglePills items={tags} selected={tagIds} onToggle={toggleTag} emptyText="No tags yet — create them on the Departments page." />
          </div>

          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button
              type="submit"
              disabled={submitting || !email.trim() || !roleId || (isSpecialist && frameworkIds.length === 0)}
              style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Edit member modal ─────────────────────────────────────────────────────────

function EditMemberModal({ member, isSelf, roles, departments, sites, tags, enrolled, onClose, onDone }: {
  member: Member;
  isSelf: boolean;
  roles: Role[];
  departments: Department[];
  sites: SiteRef[];
  tags: Tag[];
  enrolled: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const initialRoleId = member.role?.id ?? roles.find((r) => r.builtinKey === member.clientRole)?.id ?? "";
  const [roleId, setRoleId] = useState(initialRoleId);
  const [frameworkIds, toggleFramework, setFrameworkIds] = useToggleList(member.frameworkAccess ?? []);
  const [departmentIds, toggleDepartment] = useToggleList(member.departments.map((d) => d.id));
  const [siteIds, toggleSite] = useToggleList((member.sites ?? []).map((x) => x.id));
  const [tagIds, toggleTag] = useToggleList(member.tags.map((t) => t.id));
  const [jobTitle, setJobTitle] = useState(member.jobTitle ?? "");
  const [location, setLocation] = useState(member.location ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const role = roles.find((r) => r.id === roleId);
  const isSpecialist = isPerMemberRole(role?.builtinKey);
  const roleChanged = roleId !== initialRoleId;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.patch(`/organizations/members/${member.id}`, {
        ...(roleChanged && !isSelf ? { roleId } : {}),
        ...(isSpecialist ? { frameworkIds } : {}),
        departmentIds,
        siteIds,
        tagIds,
        jobTitle: jobTitle.trim() || null,
        location: location.trim() || null,
      });
      onDone();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to update member"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Edit — ${member.displayName}`} onClose={onClose}>
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <RoleFields
          roles={roles} roleId={roleId} setRoleId={(id) => { setRoleId(id); setFrameworkIds([]); }}
          frameworkIds={frameworkIds} toggleFramework={toggleFramework} enrolled={enrolled}
          disabled={isSelf} disabledHint="You cannot change your own role — ask another administrator."
        />
        {member.directoryRoleSource && (
          <div style={{ fontSize: 11, color: theme.color.warningText, marginTop: -8 }}>
            This role is set by directory sync ({member.directoryRoleSource}). A change here lasts until the next sync run; adjust the directory group or the rules to make it stick.
          </div>
        )}

        <div style={fieldStyle}>
          <label style={labelStyle}>Departments</label>
          <TogglePills items={departments} selected={departmentIds} onToggle={toggleDepartment} emptyText="No departments yet." />
          <div style={hintStyle}>Members of a department see controls tagged to it (plus untagged controls). Leave empty to see everything.</div>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Sites</label>
          <TogglePills items={sites.map((x) => ({ id: x.id, name: `${x.name} · ${x.country}` }))} selected={siteIds} onToggle={toggleSite} emptyText="No sites yet — add them on the Sites page." />
          <div style={hintStyle}>Where this member is based. Organisational only; sites never change what a member can see or do.</div>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Tags</label>
          <TogglePills items={tags} selected={tagIds} onToggle={toggleTag} emptyText="No tags yet — create them on the Departments page." />
          <div style={hintStyle}>
            Tags are labels only; they never change what a member can see or do.
            {member.tags.some((t) => t.source) && (
              <> {member.tags.filter((t) => t.source).map((t) => t.name).join(" and ")} come from the directory (office and country) and are re-applied by the next sync.</>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Job title</label>
            <input style={inputStyle} value={jobTitle} maxLength={80} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. Head of IT" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Location</label>
            <input style={inputStyle} value={location} maxLength={80} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. St Helier" />
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving || (isSpecialist && frameworkIds.length === 0)} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Role editor modal ─────────────────────────────────────────────────────────

function RoleModal({ initial, catalogue, enrolled, canGrantAdmin, onClose, onSaved }: {
  initial?: Role;
  catalogue: CatalogueGroup[];
  enrolled: Array<{ id: string; name: string }>;
  canGrantAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isSystem = !!initial?.isSystem;
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [color, setColor] = useState(initial?.color ?? "#6366f1");
  const [permissions, togglePermission] = useToggleList(initial?.permissions ?? []);
  const [frameworkIds, toggleFramework, setFrameworkIds] = useToggleList(initial?.frameworkIds ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isSpecialistRole = isPerMemberRole(initial?.builtinKey);
  const labelOf = new Map<string, string>();
  for (const g of catalogue) for (const it of g.items) labelOf.set(it.key, it.label);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const body = isSystem
        ? { description: description.trim() || null, color, frameworkIds }
        : { name: name.trim(), description: description.trim() || (initial ? null : undefined), color, permissions, frameworkIds };
      if (initial) await api.patch(`/roles/${initial.id}`, body);
      else await api.post("/roles", body);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to save role"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? `Edit role — ${initial.name}` : "New role"} onClose={onClose} width={560}>
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {isSystem && (
          <div style={{ fontSize: 12, color: theme.color.muted, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
            Built-in role. Its name and permissions are fixed; you can change its description, colour and framework restriction.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={name} required maxLength={60} disabled={isSystem} autoFocus={!isSystem} onChange={(e) => setName(e.target.value)} placeholder="e.g. Risk Analyst" />
          </div>
          <Chip name={name || "Preview"} color={color} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Description (optional)</label>
          <input style={inputStyle} value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} placeholder="What this role is for" />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Colour</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Permissions</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {catalogue.map((group) => (
              <div key={group.group}>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{group.group}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {group.items.map((item) => {
                    // Already included by a broad permission on this role: shown ticked, not stored.
                    const includedBy = item.impliedBy?.find((b) => permissions.includes(b));
                    const checked = permissions.includes(item.key) || !!includedBy;
                    const lockedAdmin = item.key === "org:admin" && !canGrantAdmin;
                    const disabled = isSystem || lockedAdmin || !!includedBy;
                    return (
                      <label
                        key={item.key}
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 10px", borderRadius: 3,
                          border: `1px solid ${checked ? theme.color.accent : theme.color.border}`,
                          background: checked ? theme.color.accentBg : "transparent",
                          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled && !checked ? 0.6 : 1,
                        }}
                      >
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={() => togglePermission(item.key)} style={{ marginTop: 2 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>{item.label}</div>
                          <div style={{ fontSize: 11, color: theme.color.subtle }}>{item.description}</div>
                          {lockedAdmin && <div style={{ fontSize: 11, color: theme.color.warningText }}>Only an administrator can grant this.</div>}
                          {includedBy && !isSystem && <div style={{ fontSize: 11, color: theme.color.muted }}>Included with {labelOf.get(includedBy) ?? includedBy}.</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {!isSystem && permissions.length === 0 && <div style={hintStyle}>No permissions selected — members of this role will have read-only access.</div>}
        </div>

        {!isSpecialistRole && (
          <div style={fieldStyle}>
            <label style={labelStyle}>Framework restriction</label>
            <TogglePills items={enrolled} selected={frameworkIds} onToggle={toggleFramework} emptyText="No frameworks enrolled yet." />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
              <span style={hintStyle}>
                {frameworkIds.length === 0
                  ? <span style={{ color: theme.color.success, fontWeight: 600 }}>Unrestricted — sees all enrolled frameworks</span>
                  : `Restricted to ${frameworkIds.length} framework${frameworkIds.length === 1 ? "" : "s"}`}
              </span>
              {frameworkIds.length > 0 && (
                <button type="button" onClick={() => setFrameworkIds([])} style={{ fontSize: 11, color: theme.color.subtle, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        {isSpecialistRole && <div style={hintStyle}>{initial?.name ?? "This role"} is restricted per member, not per role.</div>}

        {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving || (!isSystem && !name.trim())} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save role"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Delete role dialog ────────────────────────────────────────────────────────

function DeleteRoleModal({ role, roles, onClose, onDeleted }: {
  role: Role;
  roles: Role[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const others = roles.filter((r) => r.id !== role.id && r.offered !== false);
  const viewer = others.find((r) => r.builtinKey === "viewer") ?? others[0];
  const [reassignToRoleId, setReassignToRoleId] = useState(viewer?.id ?? "");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const members = role.memberCount ?? 0;

  async function handleDelete() {
    setDeleting(true); setError("");
    try {
      await api.del(`/roles/${role.id}${members > 0 ? `?reassignToRoleId=${encodeURIComponent(reassignToRoleId)}` : ""}`);
      onDeleted();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to delete role"));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal title={`Delete role — ${role.name}`} onClose={onClose} width={440}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {members > 0 ? (
          <>
            <div style={{ fontSize: 13, color: theme.color.text }}>
              {members} member{members === 1 ? " has" : "s have"} this role. Choose where to move them:
            </div>
            <select style={inputStyle} value={reassignToRoleId} onChange={(e) => setReassignToRoleId(e.target.value)}>
              {others.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </>
        ) : (
          <div style={{ fontSize: 13, color: theme.color.text }}>No members hold this role. Delete it?</div>
        )}
        {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button
            type="button" onClick={handleDelete} disabled={deleting || (members > 0 && !reassignToRoleId)}
            style={{ padding: "8px 16px", borderRadius: 3, border: "none", background: theme.color.danger, color: "#fff", fontSize: 13, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}
          >
            {deleting ? "Deleting…" : "Delete role"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Roles tab ─────────────────────────────────────────────────────────────────

function RolesTab({ data, error, enrolled, canGrantAdmin, onChanged, onRetry }: {
  data: RolesData | null;
  error: string;
  enrolled: Array<{ id: string; name: string }>;
  canGrantAdmin: boolean;
  onChanged: () => void;
  onRetry: () => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  if (!data) {
    if (error) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: theme.color.dangerText }}>
          <span>{error}</span>
          <button type="button" onClick={onRetry} style={smallBtn}>Retry</button>
        </div>
      );
    }
    return <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>;
  }

  const labels = new Map<string, string>();
  const impliedBy = new Map<string, string[]>();
  for (const g of data.catalogue) for (const it of g.items) {
    labels.set(it.key, it.label);
    if (it.impliedBy) impliedBy.set(it.key, it.impliedBy);
  }

  const system = data.roles.filter((r) => r.isSystem);
  const custom = data.roles.filter((r) => !r.isSystem);

  const cell: React.CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top" };
  const th: React.CSSProperties = { ...cell, borderBottom: "1px solid var(--c-line)", fontWeight: 600, color: theme.color.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" };

  function renderRow(r: Role) {
    // A permission a broader one on the same role already includes is not listed twice.
    const shown = r.permissions.filter((p) => !(impliedBy.get(p) ?? []).some((b) => r.permissions.includes(b)));
    const perms = shown.length ? shown.map((p) => labels.get(p) ?? p) : ["Read-only"];
    return (
      <tr key={r.id}>
        <td style={cell}>
          <Chip name={r.name} color={r.color} />
          {r.description && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4, maxWidth: 260 }}>{r.description}</div>}
        </td>
        <td style={cell}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {perms.map((p) => (
              <span key={p} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, color: theme.color.muted }}>{p}</span>
            ))}
          </div>
        </td>
        <td style={{ ...cell, fontSize: 11, color: theme.color.muted }}>
          {isPerMemberRole(r.builtinKey) ? "Per member"
            : r.frameworkIds.length === 0 ? "All enrolled"
            : r.frameworkIds.map((f) => (FRAMEWORK_LABELS as Record<string, string>)[f] ?? f).join(", ")}
        </td>
        <td style={{ ...cell, textAlign: "center", color: theme.color.muted }}>{r.memberCount ?? 0}</td>
        {data!.canManage && (
          <td style={{ ...cell, textAlign: "right" }}>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button onClick={() => setEditing(r)} style={smallBtn}>Edit</button>
              {!r.isSystem && <button onClick={() => setDeleting(r)} style={{ ...smallBtn, color: theme.color.danger }}>Delete</button>}
            </div>
          </td>
        )}
      </tr>
    );
  }

  function Section({ title, rows }: { title: string; rows: Role[] }) {
    if (rows.length === 0) return null;
    return (
      <>
        <tr>
          <td colSpan={data!.canManage ? 5 : 4} style={{ ...cell, background: theme.color.panelAlt, fontWeight: 600, fontSize: 11, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</td>
        </tr>
        {rows.map(renderRow)}
      </>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, maxWidth: 560 }}>
          Roles decide what a member can do and which frameworks they see. Built-in roles cover the common cases;
          add your own for anything more specific. Departments and tags are set per member.
        </p>
        {data.canManage && (
          <button onClick={() => setShowCreate(true)} style={{ ...btnPrimary, flexShrink: 0 }}>+ New role</button>
        )}
      </div>

      <div style={{ ...card, padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr style={{ background: theme.color.panelAlt }}>
              <th style={th}>Role</th>
              <th style={th}>Permissions</th>
              <th style={th}>Frameworks</th>
              <th style={{ ...th, textAlign: "center" }}>Members</th>
              {data.canManage && <th style={{ ...th, width: 140 }} />}
            </tr>
          </thead>
          <tbody>
            <Section title="Custom roles" rows={custom} />
            <Section title="Built-in roles" rows={system} />
          </tbody>
        </table>
      </div>

      {showCreate && (
        <RoleModal catalogue={data.catalogue} enrolled={enrolled} canGrantAdmin={canGrantAdmin} onClose={() => setShowCreate(false)} onSaved={onChanged} />
      )}
      {editing && (
        <RoleModal initial={editing} catalogue={data.catalogue} enrolled={enrolled} canGrantAdmin={canGrantAdmin} onClose={() => setEditing(null)} onSaved={onChanged} />
      )}
      {deleting && (
        <DeleteRoleModal role={deleting} roles={data.roles} onClose={() => setDeleting(null)} onDeleted={onChanged} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function TeamPage() {
  const { user: currentUser } = useCurrentUser();
  const { frameworks } = useFrameworks();
  const enrolled = useMemo(
    () => frameworks.map((f) => ({ id: f.frameworkId, name: (FRAMEWORK_LABELS as Record<string, string>)[f.frameworkId] ?? f.frameworkId })),
    [frameworks],
  );

  const [tab,          setTab]          = useState<"members" | "roles" | "directory" | "signin">("members");
  const [data,         setData]         = useState<TeamData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [rolesData,    setRolesData]    = useState<RolesData | null>(null);
  const [rolesError,   setRolesError]   = useState("");
  const [departments,  setDepartments]  = useState<Department[]>([]);
  const [sites,        setSites]        = useState<SiteRef[]>([]);
  const [tags,         setTags]         = useState<Tag[]>([]);
  const [invites,      setInvites]      = useState<InviteSummary[]>([]);
  const [showInvite,   setShowInvite]   = useState(false);
  const [editMember,   setEditMember]   = useState<Member | null>(null);
  const [confirmId,    setConfirmId]    = useState<string | null>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [deleteError,  setDeleteError]  = useState("");
  const [mfaResetId,   setMfaResetId]   = useState<string | null>(null);
  const [mfaResetting, setMfaResetting] = useState(false);
  const [mfaResetMsg,  setMfaResetMsg]  = useState<{ ok: boolean; text: string } | null>(null);

  const [search,       setSearch]       = useState("");
  const [roleFilter,   setRoleFilter]   = useState("");
  const [deptFilter,   setDeptFilter]   = useState("");
  const [tagFilter,    setTagFilter]    = useState("");
  // ?site=<id> (from a site's detail page) lists the people based there.
  const [searchParams]                  = useSearchParams();
  const [siteFilter,   setSiteFilter]   = useState(() => searchParams.get("site") ?? "");

  function loadInvites() {
    api.get<{ invites: InviteSummary[] }>("/invites").then((d) => setInvites(d.invites)).catch(() => {});
  }
  function loadMembers() {
    setLoading(true);
    api.get<TeamData>("/organizations/members").then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }
  function loadRoles() {
    setRolesError("");
    api.get<RolesData>("/roles")
      .then(setRolesData)
      .catch((err) => { setRolesData(null); setRolesError(errorMessage(err, "Could not load roles.")); });
  }
  function loadLookups() {
    api.get<{ departments: Department[] }>("/departments").then((d) => setDepartments(d.departments)).catch(() => {});
    api.get<{ sites: SiteRef[] }>("/sites").then((d) => setSites(d.sites)).catch(() => {});
    api.get<{ tags: Tag[] }>("/user-tags").then((d) => setTags(d.tags)).catch(() => {});
  }

  useEffect(() => {
    loadMembers();
    loadInvites();
    loadRoles();
    loadLookups();
  }, []);

  function refreshAll() {
    loadMembers();
    loadRoles();
  }

  // What a removed member is still named on, handed back by the removal (backend lib/ownership.ts).
  const [owned, setOwned] = useState<{ personName: string; records: OwnedRecord[] } | null>(null);
  const [ownedError, setOwnedError] = useState("");
  async function showOwnership(m: { id: string; displayName: string }) {
    setOwnedError("");
    try {
      const d = await api.get<{ records: OwnedRecord[] }>(`/organizations/members/${m.id}/ownership`);
      setOwned({ personName: m.displayName, records: d.records });
    } catch (err) {
      setOwnedError(errorMessage(err, "Could not load what they own"));
    }
  }

  async function handleDelete(id: string) {
    setDeleting(true); setDeleteError("");
    try {
      const removed = data?.members.find((m) => m.id === id);
      const res = await api.del<{ ok: true; reassign?: OwnedRecord[] }>(`/organizations/members/${id}`);
      setConfirmId(null);
      if (res?.reassign?.length) setOwned({ personName: removed?.displayName ?? "This member", records: res.reassign });
      refreshAll();
    } catch (err) {
      setDeleteError(errorMessage(err, "Failed to remove user"));
    } finally {
      setDeleting(false);
    }
  }

  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [reactivateError, setReactivateError] = useState("");
  async function handleReactivate(id: string) {
    setReactivatingId(id); setReactivateError("");
    try {
      await api.post(`/organizations/members/${id}/reactivate`);
      refreshAll();
    } catch (err) {
      setReactivateError(errorMessage(err, "Failed to reactivate the member"));
    } finally {
      setReactivatingId(null);
    }
  }

  async function handleMfaReset(id: string) {
    setMfaResetting(true); setMfaResetMsg(null);
    try {
      await api.post(`/organizations/members/${id}/mfa-reset`);
      setMfaResetId(null);
      setMfaResetMsg({ ok: true, text: "Authenticator reset. They have been signed out everywhere and will set up a new app at their next sign-in." });
      loadMembers();
    } catch (err) {
      setMfaResetMsg({ ok: false, text: errorMessage(err, "Failed to reset the authenticator") });
    } finally {
      setMfaResetting(false);
    }
  }

  const canGrantAdmin = !!currentUser?.permissions?.includes("org:admin");
  const roles = rolesData?.roles ?? [];

  const visibleMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.members ?? []).filter((m) => {
      if (q && !`${m.displayName} ${m.email} ${m.jobTitle ?? ""} ${m.location ?? ""}`.toLowerCase().includes(q)) return false;
      if (roleFilter && (m.role?.id ?? "") !== roleFilter) return false;
      if (deptFilter && !m.departments.some((d) => d.id === deptFilter)) return false;
      if (tagFilter && !m.tags.some((t) => t.id === tagFilter)) return false;
      if (siteFilter && !(m.sites ?? []).some((x) => x.id === siteFilter)) return false;
      return true;
    });
  }, [data, search, roleFilter, deptFilter, tagFilter, siteFilter]);

  // Staff carry no org role, department, tag or site, so those filters hide the section.
  const visibleStaff = useMemo(() => {
    if (roleFilter || deptFilter || tagFilter || siteFilter) return [];
    const q = search.trim().toLowerCase();
    return (data?.staff ?? []).filter((s) => !q || `${s.displayName} ${s.email} ${s.jobTitle ?? ""} ${s.location ?? ""}`.toLowerCase().includes(q));
  }, [data, search, roleFilter, deptFilter, tagFilter, siteFilter]);

  const cell: React.CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top" };
  const th: React.CSSProperties = { ...cell, borderBottom: "1px solid var(--c-line)", fontWeight: 600, color: theme.color.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" };
  const filterSelect: React.CSSProperties = { ...inputStyle, width: "auto", minWidth: 140, padding: "6px 10px", fontSize: 12 };
  const colCount = data?.isOrgAdmin ? 6 : 5;

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      {owned && <OwnedRecordsSheet personName={owned.personName} records={owned.records} onClose={() => setOwned(null)} />}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>My Team</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Users who have access to your organisation's compliance platform, and the roles they hold.
          </p>
        </div>
        {data?.isOrgAdmin && tab === "members" && (
          <button onClick={() => setShowInvite(true)} style={{ ...btnPrimary, flexShrink: 0 }}>+ Invite member</button>
        )}
      </div>

      <IndexTabs
        items={[
          { id: "members" as const, label: "Members", count: data?.members.length },
          { id: "roles" as const, label: "Roles", count: rolesData?.roles.length },
          ...(data?.isOrgAdmin ? [{ id: "directory" as const, label: "Directory sync" }, { id: "signin" as const, label: "Sign-in" }] : []),
        ]}
        value={tab}
        onChange={setTab}
        style={{ marginBottom: 20 }}
      />

      {tab === "directory" && data?.isOrgAdmin && <DirectorySyncPanel scope="org" />}
      {tab === "signin" && data?.isOrgAdmin && <SignInPolicyPanel />}

      {tab === "roles" && (
        <RolesTab data={rolesData} error={rolesError} enrolled={enrolled} canGrantAdmin={canGrantAdmin} onChanged={refreshAll} onRetry={loadRoles} />
      )}

      {tab === "members" && (
        <>
          {data?.isOrgAdmin && invites.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Invitations sent</div>
              <InviteHistoryTable invites={invites} onResent={loadInvites} onRevoke={(id) => api.post(`/invites/${id}/revoke`, {})} />
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <input style={{ ...inputStyle, width: "auto", flex: "1 1 180px", padding: "6px 10px", fontSize: 12 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, title…" />
            <select style={filterSelect} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All roles</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select style={filterSelect} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
              <option value="">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select style={filterSelect} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
              <option value="">All tags</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {sites.length > 0 && (
              <select style={filterSelect} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} title="Members based at one site">
                <option value="">All sites</option>
                {sites.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            )}
            {(search || roleFilter || deptFilter || tagFilter || siteFilter) && (
              <button type="button" onClick={() => { setSearch(""); setRoleFilter(""); setDeptFilter(""); setTagFilter(""); setSiteFilter(""); }} style={{ fontSize: 12, color: theme.color.subtle, background: "none", border: "none", cursor: "pointer" }}>
                Clear filters
              </button>
            )}
          </div>

          {mfaResetMsg?.ok && (
            <div style={{ fontSize: 12, color: theme.color.successText, background: theme.color.successBg, border: `1px solid ${theme.color.success}`, borderRadius: theme.radius.sm, padding: "8px 12px", marginBottom: 12 }}>
              {mfaResetMsg.text}
            </div>
          )}

          <div style={{ ...card, padding: 0, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
              <thead>
                <tr style={{ background: theme.color.panelAlt }}>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Role</th>
                  <th style={{ ...th, minWidth: 150 }}>Departments</th>
                  <th style={{ ...th, minWidth: 150 }}>Tags</th>
                  {data?.isOrgAdmin && <th style={{ ...th, ...stickyActions, background: theme.color.panelAlt, width: 240 }} />}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={colCount} style={{ ...cell, color: theme.color.muted, textAlign: "center", padding: "24px 16px" }}>Loading…</td></tr>
                )}
                {!loading && (!data || data.members.length === 0) && (
                  <tr><td colSpan={colCount} style={{ ...cell, color: theme.color.muted, textAlign: "center", padding: "24px 16px" }}>
                    {data?.staff?.length ? "No invited members yet — your consultancy's staff are listed below." : "No team members yet."}
                  </td></tr>
                )}
                {!loading && data && data.members.length > 0 && visibleMembers.length === 0 && (
                  <tr><td colSpan={colCount} style={{ ...cell, color: theme.color.muted, textAlign: "center", padding: "24px 16px" }}>No members match these filters.</td></tr>
                )}
                {!loading && visibleMembers.map((m) => (
                  <React.Fragment key={m.id}>
                    <tr>
                      <td style={{ ...cell, maxWidth: 220 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.displayName}{m.id === currentUser?.id && <span style={{ fontSize: 10, color: theme.color.subtle, marginLeft: 6 }}>(you)</span>}
                        </div>
                        {(m.jobTitle || m.location) && (
                          <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                            {[m.jobTitle, m.location].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td style={{ ...cell, color: theme.color.muted, maxWidth: 220 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</div>
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                          {SIGN_IN_LABEL[m.signInMethod]}{m.signInMethod === "password" && !m.totpEnabled && " · app not set up"}
                        </div>
                      </td>
                      <td style={cell}>
                        {m.role ? <Chip name={m.role.name} color={m.role.color} /> : <span style={{ fontSize: 12, color: theme.color.muted }}>—</span>}
                        {m.directoryRoleSource && (
                          <div title={m.directoryRoleSource} style={{ fontSize: 10, color: theme.color.subtle, marginTop: 3 }}>via directory sync</div>
                        )}
                        {isPerMemberRole(m.role?.builtinKey) && m.frameworkAccess.length > 0 && (
                          <div style={{ fontSize: 10, color: theme.color.subtle, marginTop: 3 }}>
                            {m.frameworkAccess.map((f) => (FRAMEWORK_LABELS as Record<string, string>)[f] ?? f).join(", ")}
                          </div>
                        )}
                      </td>
                      <td style={cell}>
                        {m.departments.length ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {m.departments.map((d) => <Chip key={d.id} name={d.name} color={d.color} small />)}
                          </div>
                        ) : <span style={{ fontSize: 12, color: theme.color.muted }}>—</span>}
                        {(m.sites ?? []).length > 0 && (
                          <div style={{ fontSize: 10, color: theme.color.subtle, marginTop: 4 }}>
                            {m.sites.map((x) => x.name).join(", ")}
                          </div>
                        )}
                      </td>
                      <td style={cell}>
                        {m.tags.length ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {m.tags.map((t) => <Chip key={t.id} name={t.name} color={t.color} small />)}
                          </div>
                        ) : <span style={{ fontSize: 12, color: theme.color.muted }}>—</span>}
                      </td>
                      {data?.isOrgAdmin && (
                        <td style={{ ...cell, ...stickyActions, textAlign: "right" }}>
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            <button onClick={() => setEditMember(m)} style={smallBtn}>Edit</button>
                            {m.id !== currentUser?.id && m.signInMethod === "password" && m.totpEnabled && (
                              <button onClick={() => { setMfaResetId(m.id); setConfirmId(null); setMfaResetMsg(null); }} style={smallBtn}>Reset MFA</button>
                            )}
                            {m.id !== currentUser?.id && (
                              <button onClick={() => { setConfirmId(m.id); setMfaResetId(null); setDeleteError(""); }} style={{ ...smallBtn, color: theme.color.danger }}>Remove</button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {mfaResetId === m.id && (
                      <tr key={`${m.id}-mfa`}>
                        <td colSpan={colCount} style={{ ...cell, background: theme.color.panelAlt }}>
                          <div style={inViewConfirm}>
                            <span style={{ fontSize: 13, color: theme.color.text }}>
                              Reset the authenticator app for <strong>{m.displayName}</strong>? Only do this once you have confirmed it is really them —
                              they will be signed out everywhere and set up a new app after their next password and emailed code.
                            </span>
                            <button
                              onClick={() => handleMfaReset(m.id)} disabled={mfaResetting}
                              style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: theme.color.accent, color: "#fff", fontSize: 12, fontWeight: 600, cursor: mfaResetting ? "not-allowed" : "pointer", opacity: mfaResetting ? 0.6 : 1 }}
                            >
                              {mfaResetting ? "Resetting…" : "Yes, reset"}
                            </button>
                            <button onClick={() => { setMfaResetId(null); setMfaResetMsg(null); }} disabled={mfaResetting} style={{ ...smallBtn, color: theme.color.muted }}>Cancel</button>
                            {mfaResetMsg && !mfaResetMsg.ok && <span style={{ fontSize: 12, color: theme.color.danger }}>{mfaResetMsg.text}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                    {confirmId === m.id && (
                      <tr key={`${m.id}-confirm`}>
                        <td colSpan={colCount} style={{ ...cell, background: theme.color.panelAlt }}>
                          <div style={inViewConfirm}>
                            <span style={{ fontSize: 13, color: theme.color.text }}>
                              Remove <strong>{m.displayName}</strong> from your organisation? Their account is deactivated and signed out everywhere;
                              their comments and records keep their name, and you can reactivate them later.
                            </span>
                            <button
                              onClick={() => handleDelete(m.id)} disabled={deleting}
                              style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: theme.color.danger, color: "#fff", fontSize: 12, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}
                            >
                              {deleting ? "Removing…" : "Yes, remove"}
                            </button>
                            <button onClick={() => { setConfirmId(null); setDeleteError(""); }} disabled={deleting} style={{ ...smallBtn, color: theme.color.muted }}>Cancel</button>
                            {deleteError && <span style={{ fontSize: 12, color: theme.color.danger }}>{deleteError}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {(data?.deactivated?.length ?? 0) > 0 && (
            <div style={{ marginTop: 24 }}>
              <DimensionHeading as="h3" count={data!.deactivated!.length} style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                Deactivated
              </DimensionHeading>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10 }}>
                Removed members cannot sign in and are not counted or offered anywhere as members. Their name stays on what they did.
              </div>
              {reactivateError && <div style={{ fontSize: 12, color: theme.color.danger, marginBottom: 8 }}>{reactivateError}</div>}
              {ownedError && <div style={{ fontSize: 12, color: theme.color.danger, marginBottom: 8 }}>{ownedError}</div>}
              <div style={{ ...card, padding: 0, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: theme.color.panelAlt }}>
                      <th style={th}>Name</th>
                      <th style={th}>Email</th>
                      <th style={th}>Role</th>
                      {data?.isOrgAdmin && <th style={{ ...th, width: 220 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {data!.deactivated!.map((m) => (
                      <tr key={m.id}>
                        <td style={{ ...cell, maxWidth: 220, color: theme.color.muted }}>
                          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.displayName}</div>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.subtle, border: `1px solid ${theme.color.border}` }}>Deactivated</span>
                        </td>
                        <td style={{ ...cell, color: theme.color.muted, maxWidth: 264, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</td>
                        <td style={{ ...cell, color: theme.color.muted }}>{m.role?.name ?? "—"}</td>
                        {data?.isOrgAdmin && (
                          <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                            <button onClick={() => showOwnership(m)} style={{ ...smallBtn, marginRight: 6 }}>What they own</button>
                            <button onClick={() => handleReactivate(m.id)} disabled={reactivatingId === m.id} style={smallBtn}>
                              {reactivatingId === m.id ? "Reactivating…" : "Reactivate"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {visibleStaff.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <DimensionHeading as="h3" count={visibleStaff.length} style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                Consultancy staff
              </DimensionHeading>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10 }}>
                {data?.consultancyName ? `${data.consultancyName} staff` : "Consultancy staff"} work in this portal through the
                consultant workspace, with the rights their consultancy role gives them. Manage them under Users there.
              </div>
              <div style={{ ...card, padding: 0, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: theme.color.panelAlt }}>
                      <th style={th}>Name</th>
                      <th style={th}>Email</th>
                      <th style={th}>Consultancy role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStaff.map((s) => (
                      <tr key={s.id}>
                        <td style={{ ...cell, maxWidth: 220 }}>
                          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.displayName}{s.id === currentUser?.id && <span style={{ fontSize: 10, color: theme.color.subtle, marginLeft: 6 }}>(you)</span>}
                          </div>
                          {(s.jobTitle || s.location) && (
                            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                              {[s.jobTitle, s.location].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </td>
                        <td style={{ ...cell, color: theme.color.muted, maxWidth: 264, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.email}</td>
                        <td style={cell}>
                          <Chip name={CONSULTANT_ROLE_LABELS[s.consultantRole] ?? s.consultantRole} color="#8b5cf6" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showInvite && (
        <InviteMemberModal
          roles={roles} departments={departments} tags={tags} enrolled={enrolled}
          onClose={() => setShowInvite(false)}
          onDone={() => { loadMembers(); loadInvites(); }}
        />
      )}
      {editMember && (
        <EditMemberModal
          member={editMember} isSelf={editMember.id === currentUser?.id}
          roles={roles} departments={departments} sites={sites} tags={tags} enrolled={enrolled}
          onClose={() => setEditMember(null)}
          onDone={refreshAll}
        />
      )}
    </div>
  );
}
